import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import {
  listNotesApi,
  updateNoteApi,
  markViewedApi,
  deleteNoteApi
} from '../../utils/notesData';
import { useDispatch } from 'react-redux';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { canDeleteContent } from '../../utils/permissions';
import AppOnboarding from '../../components/AppOnboarding';

const NotesApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [notes, setNotes] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load notes on mount in background
  useEffect(() => {
    if (activeTab !== 'home') {
      loadNotes('all', '', '');
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'home') {
      loadNotes(activeTab, searchQuery, selectedCategory);
    }
  }, [activeTab, selectedCategory, searchQuery]);

  const loadNotes = async (tab = activeTab, query = searchQuery, category = selectedCategory) => {
    if (tab === 'home') {
      return; // Don't load notes on home tab
    }
    try {
      const fetched = await listNotesApi({
        q: query || undefined,
        category: tab === 'categories' ? (category || undefined) : undefined,
        bookmarked: tab === 'bookmarked' ? true : undefined,
      });
      setNotes(fetched);
    } catch (e) {
      console.error(e);
      // Only show error if user is actively viewing this tab
      if (tab === activeTab) {
        dispatch(setErrorAlert('Failed to load notes'));
      }
    }
  };

  const categoriesWithCounts = useMemo(() => {
    const counts = {};
    notes.forEach(n => (n.categories || []).forEach(c => { counts[c] = (counts[c] || 0) + 1; }));
    return counts;
  }, [notes]);

  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'all', label: 'All' },
    { id: 'bookmarked', label: 'Bookmarked' },
    { id: 'recent', label: 'Recent' },
    { id: 'categories', label: 'Categories' }
  ];

  const filteredNotes = notes;

  const handleNoteClick = (id) => {
    try {
      markViewedApi(id);
      navigate(`/notes/${id}`);
    } catch (e) {
      console.error(e);
      dispatch(setErrorAlert('Failed to open note'));
    }
  };

  const handleBookmarkToggle = async (id, e) => {
    e.stopPropagation();
    try {
      const current = notes.find(n => n._id === id);
      const updated = await updateNoteApi(id, { bookmarked: !current?.bookmarked });
      setNotes(prev => prev.map(n => (n._id === updated._id ? updated : n)));
      dispatch(setSuccessAlert(updated.bookmarked ? 'Note bookmarked!' : 'Bookmark removed'));
    } catch (e2) {
      console.error(e2);
      dispatch(setErrorAlert('Failed to update bookmark'));
    }
  };

  const handleCreateNote = () => {
    // Navigate to the new note route without creating it yet
    navigate('/notes/new');
  };

  const handleItemSelect = (note) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(item => item._id === note._id);
      if (isSelected) {
        return prev.filter(item => item._id !== note._id);
      } else {
        return [...prev, note];
      }
    });
  };

  const handleSelectAll = () => {
    const allSelected = filteredNotes.every(note => selectedItems.some(item => item._id === note._id));
    
    if (allSelected) {
      // Deselect all current items
      setSelectedItems(prev => prev.filter(item => !filteredNotes.some(note => note._id === item._id)));
    } else {
      // Select all current items
      const newSelections = filteredNotes.filter(note => !selectedItems.some(item => item._id === note._id));
      setSelectedItems(prev => [...prev, ...newSelections]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    setIsDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const item of selectedItems) {
      try {
        await deleteNoteApi(item._id);
        successCount++;
      } catch (error) {
        console.error(`Failed to delete note ${item.title}:`, error);
        failCount++;
      }
    }

    if (successCount > 0) {
      dispatch(setSuccessAlert(`Successfully deleted ${successCount} note(s)${failCount > 0 ? `, ${failCount} failed` : ''}`));
      setSelectedItems([]);
      loadNotes(); // Reload data
    } else {
      dispatch(setErrorAlert('Failed to delete selected notes. Please try again.'));
    }

    setIsDeleting(false);
    setShowDeleteConfirm(false);
  };


  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Notes</span>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowSearch(!showSearch)}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Notes
              </button>
              
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={handleCreateNote}
              >
                New Note
              </span>
              
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
              >
                Go back
              </span>
            </div>
          </div>

          {showSearch && (
            <div className="w-full px-4 mb-4">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-[#F5F5F5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Search Notes</span>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search your notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-black border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#F5F5F5]"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="w-full px-4 mb-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#F5F5F5]"
              >
                <option value="">All Categories</option>
                {Object.entries(categoriesWithCounts).map(([name, count]) => (
                  <option key={name} value={name}>
                    {name} ({count})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="w-full mb-6 px-4">
            <div className="flex overflow-x-auto scrollbar-hide">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F5F5F5]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">
            {activeTab === 'home' ? (
              <div className="flex flex-col gap-4 mb-8">
                {/* Onboarding - shows when no notes */}
                {notes.length === 0 && (
                  <div className="pt-4">
                    <AppOnboarding
                      appName="Notes"
                      description="Capture ideas, meeting notes, and documents. AI helps you organize and find them instantly."
                      color="#F5F5F5"
                      steps={[
                        {
                          id: 'create-note',
                          title: 'Create your first note',
                          description: 'Start writing or paste content',
                          completed: false,
                          onClick: handleCreateNote
                        },
                        {
                          id: 'organize',
                          title: 'Organize with categories',
                          description: 'Add tags for easy search',
                          completed: false,
                          onClick: handleCreateNote
                        }
                      ]}
                      actionText="Create First Note"
                      onAction={handleCreateNote}
                      icon={
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      }
                    />
                  </div>
                )}
                
                <div className="mb-4 pt-8">
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5] mb-6">
                    Drop in any notes, documents, and ideas. Let DIZEE organize and use AI to recall notes instantly.
                  </p>
                </div>

                <div
                  onClick={handleCreateNote}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#F5F5F5] hover:bg-[rgba(245,245,245,0.05)] transition-all cursor-pointer"
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Create & Organize</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Create notes for meetings, ideas, contracts, or anything important. Organize with categories and tags for easy retrieval.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">AI-Powered Search</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Search across all your notes with natural language. Find what you need instantly, even if you don't remember the exact words.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Bookmarks & Categories</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Bookmark important notes for quick access. Use categories to organize notes by project, meeting type, or topic.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Smart Recall</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    DIZEE's AI remembers context from your notes, making it easy to find related information and recall important details.
                  </p>
                </div>
              </div>
            ) : filteredNotes.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-normal mb-2">No notes found</h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  {searchQuery ? `No notes match "${searchQuery}".` : 'Create your first note to get started.'}
                </p>
                <button 
                  onClick={handleCreateNote}
                  className="bg-[#F5F5F5] text-black px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(245,245,245,0.9)] transition-colors"
                >
                  New Note
                </button>
              </div>
            ) : (
              <>
                {/* Select All & Delete Buttons */}
                {filteredNotes.length > 0 && canDeleteContent('notes') && (
                  <div className="flex items-center justify-end px-4 mb-4 gap-3">
                    <button
                      onClick={handleSelectAll}
                      className="text-[#F5F5F5] text-[10px] hover:text-[rgba(245,245,245,0.8)] transition-colors"
                    >
                      {filteredNotes.every(note => selectedItems.some(item => item._id === note._id)) ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedItems.length > 0 && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="bg-[#FF2300] text-white text-[10px] px-3 py-1.5 rounded-lg hover:bg-[#FF2300]/90 transition-colors"
                      >
                        Delete ({selectedItems.length})
                      </button>
                    )}
                  </div>
                )}

                <div className="grid gap-4">
                  {filteredNotes.map((note) => {
                    const isSelected = selectedItems.some(item => item._id === note._id);
                    return (
                      <div
                        key={note._id}
                        className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 transition-all cursor-pointer ${
                          isSelected 
                            ? 'border-[#F5F5F5] bg-[rgba(245,245,245,0.1)]' 
                            : 'border-[rgba(255,255,255,0.1)] hover:border-[#F5F5F5] hover:bg-[rgba(245,245,245,0.05)]'
                        }`}
                        onClick={() => canDeleteContent('notes') ? handleItemSelect(note) : handleNoteClick(note._id)}
                        style={{ cursor: canDeleteContent('notes') ? 'pointer' : 'default' }}
                      >
                        <div className="flex items-start gap-4">
                          {/* Note Icon/Indicator */}
                          <div className="flex flex-col items-center min-w-[60px]">
                            <div className="w-12 h-12 bg-[rgba(245,245,245,0.2)] rounded-lg flex items-center justify-center">
                              <svg className="w-6 h-6 text-[#F5F5F5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          </div>
                          
                          {/* Note Info */}
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-white text-[12px] font-normal break-words">
                                {note.title || 'Untitled Note'}
                              </h3>
                              <div className="flex items-center gap-2">
                                {canDeleteContent('notes') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNoteClick(note._id);
                                    }}
                                    className="text-[#F5F5F5] text-[10px] px-2 py-1 hover:bg-[rgba(245,245,245,0.1)] rounded transition-colors"
                                  >
                                    Edit
                                  </button>
                                )}
                                <button
                                  onClick={(e) => handleBookmarkToggle(note._id, e)}
                                  className={`p-1 rounded transition-colors ${
                                    note.bookmarked
                                      ? 'text-yellow-400 hover:text-yellow-300'
                                      : 'text-[rgba(255,255,255,0.3)] hover:text-yellow-400'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill={note.bookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                  </svg>
                                </button>
                              </div>
                            </div>

                            <p className="text-[rgba(255,255,255,0.6)] text-[11px] mb-2 leading-tight break-words">
                              {(note.content || '').replace(/\n/g, ' ').slice(0, 120)}{(note.content || '').length > 120 ? '…' : ''}
                            </p>

                            {/* Tags and metadata row */}
                            <div className="flex flex-wrap items-center gap-2">
                              {/* Date and view count */}
                              <span className="text-[10px] text-[rgba(255,255,255,0.4)]">
                                {new Date(note.updatedAt).toLocaleDateString()}
                              </span>
                              {note.viewCount > 0 && (
                                <span className="text-[10px] text-[rgba(255,255,255,0.4)]">
                                  {note.viewCount} views
                                </span>
                              )}
                              
                              {/* Category tags */}
                              {note.categories?.slice(0, 3).map((cat) => (
                                <span key={cat} className="text-[9px] px-2 py-1 rounded bg-[rgba(245,245,245,0.2)] text-[rgba(245,245,245,1)] border border-[rgba(245,245,245,0.3)]">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 relative">
              <h3 className="text-white text-[16px] font-normal text-left">Delete Selected Notes</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <p className="text-white text-[14px] mb-6">
              Are you sure you want to delete {selectedItems.length} note{selectedItems.length > 1 ? 's' : ''}? This action cannot be undone.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="flex-1 bg-[#FF2300] text-white text-[12px] font-normal py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-normal py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutHeader>
  );
};

export default NotesApp;


